import { Injectable, NgZone, RendererFactory2, Inject, ViewEncapsulation } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable()
export class LoadScriptService {
    private _scripts: Map<string, { name: string; globalId?: string }> = new Map();

    constructor(private _zone: NgZone, private _rendererFactory: RendererFactory2, @Inject(DOCUMENT) private _doc) {}

    public load(name: string, path: string, globalId?: string): Promise<string> {
        return new Promise((resolve, reject) => {
            let _isLoaded = false;
            if (globalId != null) {
                _isLoaded = !!window[globalId] || this._scripts.has(path);
                if (_isLoaded && !this._scripts.has(path)) {
                    this._scripts.set(path, { name: name, globalId: globalId });
                }
            } else {
                _isLoaded = this._scripts.has(path);
            }
            if (!_isLoaded) {
                this._scripts.set(path, { name: name, globalId: globalId });
                const script = this._doc.createElement('script');
                script.type = 'text/javascript';
                script.id = name;
                script.src = path;
                this._doc.body.appendChild(script);
                if (script.readyState) {
                    script.onreadystatechange = () => {
                        if (script.readyState === 'loaded' || script.readyState === 'complete') {
                            script.onreadystatechange = null;
                            resolve('Loaded');
                        }
                    };
                } else {
                    script.onload = () => {
                        resolve('Loaded');
                    };
                }
                script.onerror = (error: any) => reject(error);
            } else {
                resolve('Already loaded or still loading');
            }
        });
    }

    public isLoaded(name: string): boolean {
        return this._scripts.has(name);
    }

    public add(tag: ScriptDefinition, forceCreation = false): void {
        try {
            const renderer = this._rendererFactory.createRenderer(this._doc, {
                id: '-1',
                encapsulation: ViewEncapsulation.None,
                styles: [],
                data: {}
            });

            const link = renderer.createElement('script');
            const head = this._doc.head;
            const selector = this._parseSelector(tag);

            if (head == null) {
                throw new Error('<head> not found within DOCUMENT.');
            }

            Object.keys(tag).forEach((prop: string) => {
                return renderer.setAttribute(link, prop, tag[prop]);
            });

            let _canAppendChild = true;

            // remove if link exists
            let _link: any;
            if ((_link = this._doc.querySelector(selector)) && forceCreation) {
                if (_link.parentNode) {
                    _link.parentNode.removeChild(_link);
                }
            } else {
                _canAppendChild = false;
            }
            if (_canAppendChild) {
                renderer.appendChild(head, link);
            }
        } catch (e) {
            console.error('Error within linkService : ', e);
        }
    }

    private _parseSelector(tag: ScriptDefinition): string {
        const attr = 'src';
        return `[${attr}="${tag[attr]}"]`;
    }
}

export declare type ScriptDefinition = {
    src: string;
    type?: string;
    charset?: string;
    defer?: boolean;
    async?: boolean;
    crossOrigin?: string;
    text?: string;
    noModule?: boolean;
    referrerPolicy?: string;
} & {
    [prop: string]: string;
};
